import type { DataHealth, HealthSource } from '@ace-tracker/shared';
import { timeAgo } from '../health';

/**
 * The header's freshness line: the latest telemetry actually in the selected
 * range, and how long ago the dashboard last queried it. Replaces a line that
 * showed when the page was generated, which looked like a data timestamp.
 *
 * `sources` narrows "latest telemetry" to the sources the current view shows;
 * omitted, it is the latest of any AI tool.
 */
export function DataFreshness({ health, sources }: { health?: DataHealth; sources?: HealthSource[] }) {
  if (!health) return <>&nbsp;</>;

  const observed = sources
    ? health.sources
        .filter((s) => sources.includes(s.source) && s.lastObserved !== null)
        .map((s) => s.lastObserved as string)
        .sort()
        .pop() ?? null
    : health.dataAsOf;

  return (
    <>
      {observed ? (
        <>
          Data as of{' '}
          <b className="font-medium text-text-muted" title="Latest telemetry received in the selected range">
            {new Date(observed).toLocaleString()}
          </b>
        </>
      ) : (
        <>No telemetry in this range</>
      )}
      <span aria-hidden="true"> · </span>
      <span title={`Queried ${new Date(health.queriedAt).toLocaleString()}. Results are cached for a few minutes.`}>
        queried {timeAgo(health.queriedAt)}
      </span>
    </>
  );
}
